import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import PublicShell from "@/components/public/PublicShell";
import { ArrowRight, ArrowLeft, ClipboardCheck, RotateCcw } from "lucide-react";

/**
 * Financial Organisation Checkup — 10 short questions, client-side only.
 * NO server storage. NO bank details. NO advice. Measures ORGANISATION,
 * never wealth / creditworthiness / investment quality.
 */
const QUESTIONS = [
    { dim: "Documents",         q: "Could a family member find your home insurance policy tonight, without asking you?" },
    { dim: "Subscriptions",     q: "Could you name every subscription that will renew this month?" },
    { dim: "Renewals",          q: "Do you know when your next annual renewal falls (insurance, memberships, warranties)?" },
    { dim: "Payments",          q: "Do you know your credit card statement dates and payment dates by heart?" },
    { dim: "Net Worth",         q: "Do you know your net worth to within about 15%?" },
    { dim: "Assets & Liabilities", q: "Could you list every loan or borrowing your household is currently paying off?" },
    { dim: "Financial Calendar", q: "Is there one place — not your memory — where household financial dates live?" },
    { dim: "Record Accessibility", q: "If your phone was lost tomorrow, could you still access your important financial records?" },
    { dim: "Family Organisation", q: "Have you and your partner (if applicable) explicitly agreed on shared vs private finances?" },
    { dim: "Continuity",        q: "Does one document exist that explains where all your important financial information lives?" },
];

const OPTIONS = [
    { label: "Yes, easily",     score: 10 },
    { label: "Mostly",          score: 7  },
    { label: "Sort of",         score: 4  },
    { label: "Not really",      score: 1  },
];

function verdict(score) {
    if (score >= 85) return { tag: "Excellent",  tone: "ok",   note: "Your financial life is already well organised. Keep the quarterly rhythm." };
    if (score >= 65) return { tag: "Good",       tone: "ok",   note: "A solid foundation. A few important areas could be easier to manage." };
    if (score >= 40) return { tag: "Developing", tone: "warn", note: "The bones are there. A weekend of organisation would move you meaningfully forward." };
    return               { tag: "Early stage", tone: "info", note: "This is where most households start. The first pass is always the hardest — and the most valuable." };
}

const STRENGTH_ADVICE = {
    Documents:             { do: "Documents are where organisation compounds fastest.",  read: "family-vault-checklist", feature: "/documents" },
    Subscriptions:         { do: "The subscriptions muscle is quiet money saved.",       read: "subscriptions",           feature: "/subscriptions" },
    Renewals:              { do: "Renewals rarely surprise organised households.",       read: "subscriptions",           feature: "/calendar" },
    Payments:              { do: "Payment discipline prevents most late-fee stories.",   read: "information-problem",     feature: "/payments" },
    "Net Worth":           { do: "Net-worth literacy is a rare and useful habit.",       read: "bank-balance-vs-net-worth", feature: "/net-worth" },
    "Assets & Liabilities":{ do: "Knowing what you own and owe puts you ahead of most.", read: "bank-balance-vs-net-worth", feature: "/net-worth" },
    "Financial Calendar":  { do: "A shared calendar quietly prevents the missed things.", read: "information-problem",     feature: "/calendar" },
    "Record Accessibility":{ do: "Accessibility is a form of security most people miss.", read: "family-vault-checklist", feature: "/documents" },
    "Family Organisation": { do: "Explicit household design saves years of friction.",   read: "couples-money-privacy",   feature: "/together" },
    Continuity:            { do: "The continuity document is rare — and priceless.",     read: "family-vault-checklist", feature: "/documents" },
};

export default function CheckupTool() {
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState(Array(QUESTIONS.length).fill(null));
    const done = step === QUESTIONS.length;

    const { score, weak, strong } = useMemo(() => {
        const scored = answers.map((a, i) => a === null ? 0 : OPTIONS[a].score);
        const total  = scored.reduce((s, n) => s + n, 0);
        const paired = QUESTIONS.map((q, i) => ({ dim: q.dim, s: scored[i] }));
        return {
            score: Math.round(total),
            weak:   paired.filter(p => p.s <= 4).map(p => p.dim),
            strong: paired.filter(p => p.s >= 8).map(p => p.dim),
        };
    }, [answers]);

    const pick = (optIdx) => {
        const next = [...answers]; next[step] = optIdx; setAnswers(next);
        setTimeout(() => setStep(step + 1), 220);   // small delay for visual feedback
    };
    const back  = () => setStep(Math.max(0, step - 1));
    const reset = () => { setStep(0); setAnswers(Array(QUESTIONS.length).fill(null)); };

    const v = verdict(score);
    const primaryDim = weak[0] || strong[0];
    const primaryCta = primaryDim && STRENGTH_ADVICE[primaryDim];

    return (
        <PublicShell>
            <div className="fc-root" data-testid="checkup-tool">
                <style>{FC_CSS}</style>

                <div className="fc-hero">
                    <div className="fc-container">
                        <Link to="/tools" className="fc-back" data-testid="checkup-back">
                            <ArrowLeft size={13}/> VAULT Tools
                        </Link>
                        <div className="fc-eyebrow">
                            <ClipboardCheck size={12} strokeWidth={2.2}/> FINANCIAL ORGANISATION CHECKUP
                        </div>
                        <h1 className="fc-title">How organised is your financial life?</h1>
                        <p className="fc-lede">
                            Ten short questions. About two minutes. No bank details. No account required.
                            We measure organisation only — never wealth, creditworthiness, or investment quality.
                        </p>
                    </div>
                </div>

                <div className="fc-container fc-body">
                    {!done ? (
                        <section className="fc-card" data-testid="checkup-question">
                            <div className="fc-progress">
                                <div className="fc-progress__fill" style={{ width: `${(step / QUESTIONS.length) * 100}%` }}/>
                            </div>
                            <div className="fc-step-meta">
                                <span>QUESTION {step + 1} OF {QUESTIONS.length}</span>
                                <span className="fc-step-dim">{QUESTIONS[step].dim.toUpperCase()}</span>
                            </div>
                            <h2 className="fc-q" data-testid={`checkup-q-${step}`}>{QUESTIONS[step].q}</h2>
                            <div className="fc-opts" role="radiogroup">
                                {OPTIONS.map((o, i) => (
                                    <button key={o.label} type="button" role="radio" aria-checked={answers[step] === i}
                                            onClick={() => pick(i)} className="fc-opt"
                                            data-testid={`checkup-opt-${i}`}>
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                            {step > 0 && (
                                <button onClick={back} className="fc-back-btn" data-testid="checkup-prev">
                                    <ArrowLeft size={13}/> Previous
                                </button>
                            )}
                        </section>
                    ) : (
                        <section className="fc-result" data-testid="checkup-result">
                            <div className="fc-score">
                                <div className={`fc-score__ring fc-score__ring--${v.tone}`}>
                                    <div className="fc-score__num" data-testid="checkup-score">{score}</div>
                                    <div className="fc-score__den">/ 100</div>
                                </div>
                                <div className="fc-score__meta">
                                    <div className="fc-eyebrow">YOUR VAULT ORGANISATION SCORE</div>
                                    <div className="fc-score__tag" data-testid="checkup-verdict">{v.tag}</div>
                                    <p className="fc-score__note">{v.note}</p>
                                </div>
                            </div>

                            {strong.length > 0 && (
                                <div className="fc-sec" data-testid="checkup-strong">
                                    <div className="fc-sec__title">What you&rsquo;re doing well</div>
                                    <ul>{strong.map(s => <li key={s}>{s}</li>)}</ul>
                                </div>
                            )}
                            {weak.length > 0 && (
                                <div className="fc-sec" data-testid="checkup-weak">
                                    <div className="fc-sec__title">What may need attention</div>
                                    <ul>{weak.map(w => <li key={w}>{w}</li>)}</ul>
                                </div>
                            )}

                            {primaryCta && (
                                <div className="fc-relevant" data-testid="checkup-relevant">
                                    <div className="fc-eyebrow">RELATED READING</div>
                                    <p className="fc-relevant__quote">&ldquo;{primaryCta.do}&rdquo;</p>
                                    <div className="fc-relevant__row">
                                        <Link to={`/learn/articles/${primaryCta.read}`} className="fc-btn fc-btn--ghost">
                                            Read more <ArrowRight size={12}/>
                                        </Link>
                                        <Link to={primaryCta.feature} className="fc-btn fc-btn--gold" data-testid="checkup-relevant-cta">
                                            See it in VAULT <ArrowRight size={12}/>
                                        </Link>
                                    </div>
                                </div>
                            )}

                            <div className="fc-finale">
                                <h3 className="fc-finale__title">Ready to organise your financial life?</h3>
                                <p className="fc-finale__sub">Save this checkup into your own VAULT and start with the areas that matter.</p>
                                <div className="fc-finale__row">
                                    <Link to="/signup" className="fc-btn fc-btn--gold" data-testid="checkup-signup">
                                        Create Your VAULT <ArrowRight size={14}/>
                                    </Link>
                                    <button onClick={reset} className="fc-btn fc-btn--ghost" data-testid="checkup-retake">
                                        <RotateCcw size={12}/> Retake
                                    </button>
                                </div>
                            </div>

                            <p className="fc-note">
                                Your answers were kept entirely in your browser and were never transmitted to VAULT.
                            </p>
                        </section>
                    )}
                </div>
            </div>
        </PublicShell>
    );
}

const FC_CSS = `
.fc-root { background: #FBF7EE; color: #0F1E3A; min-height: 100vh; padding-bottom: 96px; }
.fc-hero { background: linear-gradient(140deg,#0F1E3A 0%,#0A1424 100%); color: #F1F5F9; padding: 72px 28px 48px; }
.fc-container { max-width: 780px; margin: 0 auto; padding: 0 28px; }
.fc-back { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: rgba(241,245,249,0.7); text-decoration: none; margin-bottom: 20px; }
.fc-back:hover { color: #E6D5A8; }
.fc-eyebrow { display: inline-flex; align-items: center; gap: 6px; font-size: 10.5px; letter-spacing: 0.24em; text-transform: uppercase; font-weight: 700; color: #C6A86B; margin-bottom: 14px; }
.fc-title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: clamp(34px, 5vw, 52px); font-weight: 500; letter-spacing: -0.025em; line-height: 1.04; color: #F1F5F9; margin: 0 0 18px; }
.fc-lede { font-size: 15.5px; line-height: 1.6; color: rgba(241,245,249,0.78); max-width: 620px; margin: 0; }
.fc-body { padding-top: 40px; }
.fc-card { background: #FFFFFF; border: 1px solid rgba(15,30,58,0.10); border-radius: 24px; padding: 28px 32px; }
.fc-progress { height: 4px; background: rgba(15,30,58,0.08); border-radius: 999px; overflow: hidden; margin-bottom: 20px; }
.fc-progress__fill { height: 100%; background: linear-gradient(90deg,#C6A86B,#E6D5A8); transition: width 240ms cubic-bezier(0.16,1,0.3,1); }
.fc-step-meta { display: flex; justify-content: space-between; font-size: 10.5px; letter-spacing: 0.24em; text-transform: uppercase; font-weight: 700; color: #78716C; margin-bottom: 14px; }
.fc-step-dim { color: #C6A86B; }
.fc-q { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: clamp(24px, 3vw, 30px); font-weight: 500; letter-spacing: -0.015em; line-height: 1.25; color: #0F1E3A; margin: 0 0 24px; }
.fc-opts { display: flex; flex-direction: column; gap: 10px; }
.fc-opt { padding: 14px 20px; border-radius: 14px; border: 1px solid rgba(15,30,58,0.12); background: #FBF7EE; text-align: left; font-size: 14.5px; font-weight: 500; color: #0F1E3A; cursor: pointer; transition: background-color 180ms cubic-bezier(0.16,1,0.3,1), border-color 180ms cubic-bezier(0.16,1,0.3,1), transform 180ms cubic-bezier(0.16,1,0.3,1); }
.fc-opt:hover { border-color: #C6A86B; transform: translateX(2px); }
.fc-back-btn { display: inline-flex; align-items: center; gap: 6px; margin-top: 18px; padding: 6px 10px; background: transparent; border: none; color: #78716C; font-size: 12.5px; cursor: pointer; }
.fc-back-btn:hover { color: #0F1E3A; }
.fc-result { display: flex; flex-direction: column; gap: 24px; }
.fc-score { display: flex; gap: 24px; align-items: center; padding: 28px; background: #FFFFFF; border: 1px solid rgba(15,30,58,0.10); border-radius: 24px; flex-wrap: wrap; }
.fc-score__ring { width: 120px; height: 120px; border-radius: 999px; display: grid; place-items: center; flex-direction: column; flex-shrink: 0; background: #FBF7EE; border: 4px solid #C6A86B; text-align: center; padding-top: 8px; }
.fc-score__ring--ok   { border-color: #10B981; }
.fc-score__ring--warn { border-color: #F59E0B; }
.fc-score__ring--info { border-color: #3B82F6; }
.fc-score__num { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: 44px; font-weight: 500; line-height: 1; color: #0F1E3A; }
.fc-score__den { font-size: 11px; letter-spacing: 0.2em; color: #78716C; margin-top: 2px; }
.fc-score__meta { flex: 1; min-width: 240px; }
.fc-score__tag { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: 30px; font-weight: 500; color: #0F1E3A; margin: 4px 0 8px; }
.fc-score__note { font-size: 14.5px; line-height: 1.55; color: #57534E; margin: 0; }
.fc-sec { padding: 22px 24px; background: #FFFFFF; border: 1px solid rgba(15,30,58,0.08); border-radius: 18px; }
.fc-sec__title { font-size: 13px; font-weight: 600; color: #0F1E3A; margin-bottom: 10px; }
.fc-sec ul { list-style: none; padding: 0; margin: 0; display: flex; flex-wrap: wrap; gap: 8px; }
.fc-sec ul li { padding: 6px 12px; background: #FBF7EE; border-radius: 999px; font-size: 12.5px; color: #0F1E3A; }
.fc-relevant { padding: 26px 28px; background: linear-gradient(140deg,#FBF7EE,#F3EBD3); border: 1px solid rgba(198,168,107,0.32); border-radius: 20px; }
.fc-relevant__quote { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: 22px; font-weight: 500; letter-spacing: -0.01em; color: #0F1E3A; margin: 4px 0 16px; }
.fc-relevant__row { display: flex; gap: 10px; flex-wrap: wrap; }
.fc-finale { padding: 28px 32px; background: linear-gradient(140deg,#0F1E3A,#0A1424); color: #F1F5F9; border-radius: 24px; text-align: center; }
.fc-finale__title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: 28px; font-weight: 500; color: #F1F5F9; margin: 0 0 8px; }
.fc-finale__sub { font-size: 14.5px; line-height: 1.55; color: rgba(241,245,249,0.78); max-width: 480px; margin: 0 auto 18px; }
.fc-finale__row { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; }
.fc-btn { display: inline-flex; align-items: center; gap: 6px; padding: 10px 18px; border-radius: 999px; font-size: 13px; font-weight: 600; text-decoration: none; cursor: pointer; border: none; }
.fc-btn--gold { background: linear-gradient(180deg,#E6D5A8,#C6A86B); color: #0F1E3A; box-shadow: 0 8px 18px -6px rgba(198,168,107,0.55); }
.fc-btn--ghost { background: transparent; color: #F1F5F9; border: 1px solid rgba(241,245,249,0.24); }
.fc-finale .fc-btn--ghost:hover { background: rgba(241,245,249,0.06); }
.fc-relevant .fc-btn--ghost { color: #0F1E3A; border-color: rgba(15,30,58,0.24); }
.fc-relevant .fc-btn--ghost:hover { background: rgba(15,30,58,0.06); }
.fc-note { font-size: 11.5px; color: #78716C; text-align: center; margin: 0; }
`;
